import { ReactNode, useCallback, useEffect, useState } from "react";
import useSessionStorage from "../../hooks/useSessionStorage";
import IOidcConfig from "../../types/interfaces/IOidcConfig";
import { OidcConfigListContext } from "../../contexts/OidcConfigListContext";
import { getOidcConfigurations } from "../../functions/requests/getOidcConfigurations";

const OIDC_CONFIG_VERSION = "v2"; // Increment this to force cache clearing

export const OidcConfigListProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [oidcConfigList, setOidcConfigList] = useSessionStorage<IOidcConfig[]>(
    `oidcConfigList_${OIDC_CONFIG_VERSION}`,
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Clear old cached configs
  useEffect(() => {
    const oldKeys = Object.keys(sessionStorage).filter(key => 
      key.startsWith('oidcConfigList_') && key !== `oidcConfigList_${OIDC_CONFIG_VERSION}`
    );
    oldKeys.forEach(key => sessionStorage.removeItem(key));
  }, []);

  const reloadOidcConfigList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const oidcConfigResponse = await getOidcConfigurations();
      setOidcConfigList((_) =>
        oidcConfigResponse.map((c) => {
          const config: any = {
            name: c.name,
            client_id: c.client_id,
            redirect_uri: window.location.origin + c.redirect_uri,
            silent_redirect_uri: window.location.origin + c.silent_redirect_uri,
            scope: c.scope,
            authority: c.authority,
            service_worker_only: false,
            demonstrating_proof_of_possession: false,
          };
          
          // Include client_secret in token_request_extras if it exists (needed for some OIDC providers like Google Workspace)
          if (c.client_secret) {
            config.token_request_extras = {
              client_secret: c.client_secret
            };
          }
          
          return config;
        }),
      );
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadOidcConfigList();
  }, [reloadOidcConfigList]);

  return (
    <OidcConfigListContext.Provider
      value={{
        oidcConfigList: oidcConfigList,
        loading: loading,
        error: error,
        reload: reloadOidcConfigList,
        name: "OIDC configurations",
      }}
    >
      {children}
    </OidcConfigListContext.Provider>
  );
};
